// Workflow Kanban + Tabla. 4 macro-fases colapsables, 26 etapas — diseño dinámico con colores.
import { useEffect, useState, useMemo } from "react";
import { ChevronDown, ChevronRight, MessageSquare, Calendar, AlertCircle, X, Clock, User as UserIcon } from "lucide-react";
import { api } from "@/lib/api";

const STAGE_LABELS = {
  lead_created: "Lead creado", client_loaded: "Cliente cargado", brand_identified: "Marca identificada",
  agency_identified: "Agencia identificada", need_detected: "Necesidad detectada",
  products_selected: "Productos seleccionados", package_built: "Paquete armado",
  proposal_drafting: "Propuesta en armado", design_in_progress: "Diseño en progreso",
  proposal_sent: "Propuesta enviada", agency_reviewing: "Agencia revisa",
  agency_presenting: "Agencia presenta", client_evaluating: "Cliente evalúa",
  feedback_received: "Devolución recibida", negotiating: "Negociación",
  products_adjustment: "Ajuste productos", price_adjustment: "Ajuste precios",
  discount_decision: "Descuento decisión", proposal_approved: "Propuesta aprobada",
  contract_po: "Contrato / OC", deal_closed: "Trato cerrado",
  campaign_active: "Campaña activa", campaign_finished: "Campaña finalizada",
  billing_pending: "Cobranza pendiente", billed_paid: "Cobrado", renewal: "Renovación",
};

const PRIORITY_RING = {
  low: "ring-zinc-400/50", normal: "ring-blue-500/60", high: "ring-amber-500/70", urgent: "ring-red-500/80",
};
const PRIORITY_DOT = {
  low: "bg-zinc-400", normal: "bg-blue-500", high: "bg-amber-500", urgent: "bg-red-500",
};

// macro fase → palette { bar, soft, text, accent }
const MACRO_THEMES = {
  "Lead & Armado": { bar: "from-blue-500 to-cyan-400", soft: "bg-blue-500/5", text: "text-blue-500", border: "border-blue-500/30", chip: "bg-blue-500/10 text-blue-500" },
  "Negociación": { bar: "from-amber-500 to-orange-400", soft: "bg-amber-500/5", text: "text-amber-500", border: "border-amber-500/30", chip: "bg-amber-500/10 text-amber-500" },
  "Cierre": { bar: "from-emerald-500 to-teal-400", soft: "bg-emerald-500/5", text: "text-emerald-500", border: "border-emerald-500/30", chip: "bg-emerald-500/10 text-emerald-500" },
  "Post-venta": { bar: "from-violet-500 to-fuchsia-500", soft: "bg-violet-500/5", text: "text-violet-500", border: "border-violet-500/30", chip: "bg-violet-500/10 text-violet-500" },
};
const FALLBACK_THEME = { bar: "from-zinc-500 to-zinc-400", soft: "bg-secondary/20", text: "text-foreground", border: "border-border", chip: "bg-secondary text-foreground" };

const initialsOf = (name = "") => (name || "").split(/\s+/).filter(Boolean).map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "—";
const colorFromString = (s = "") => {
  const palette = ["#FF0055", "#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#EC4899", "#06B6D4", "#84CC16"];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
};
const dueState = (d) => {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(d); due.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff < 0) return { kind: "overdue", days: -diff, cls: "bg-red-500/15 text-red-500 border-red-500/30" };
  if (diff <= 2) return { kind: "soon", days: diff, cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" };
  return { kind: "ok", days: diff, cls: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" };
};

export default function WorkflowPage() {
  const [view, setView] = useState("kanban");
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState({ macro: {}, stages: [] });
  const [open, setOpen] = useState({ "Lead & Armado": true, "Negociación": true, "Cierre": true, "Post-venta": true });
  const [selectedCard, setSelectedCard] = useState(null);
  const [colorMode, setColorMode] = useState("phase"); // phase | owner | priority
  const [dragOver, setDragOver] = useState(null);

  const load = () => api.get("/admin/com/workflow").then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/admin/com/workflow/stages").then(setStages).catch(() => {});
  }, []);

  const stageToMacro = useMemo(() => {
    const m = {};
    Object.entries(stages.macro || {}).forEach(([macro, sts]) => sts.forEach((s) => { m[s] = macro; }));
    return m;
  }, [stages]);

  const byStage = useMemo(() => {
    const m = {};
    (stages.stages || []).forEach((s) => { m[s] = []; });
    items.forEach((it) => { (m[it.stage] = m[it.stage] || []).push(it); });
    return m;
  }, [items, stages]);

  const moveCard = async (cardId, newStage) => {
    await api.put(`/admin/com/workflow/${cardId}`, { stage: newStage });
    load();
  };

  const onDragStart = (e, card) => { e.dataTransfer.setData("card_id", card.id); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    const cardId = e.dataTransfer.getData("card_id");
    if (cardId) moveCard(cardId, stage);
  };

  const cardAccent = (card) => {
    if (colorMode === "priority") return colorFromPriority(card.priority);
    if (colorMode === "owner") return colorFromString(card.owner_email || card.assignee || card.created_by || "—");
    const macro = stageToMacro[card.stage] || "";
    return colorFromMacro(macro);
  };

  return (
    <div data-testid="commercial-workflow">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Workflow Comercial</h1>
          <p className="text-sm text-muted-foreground">{items.length} oportunidades · 26 etapas en 4 macro-fases · drag & drop</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex border border-border rounded-sm overflow-hidden text-xs" data-testid="kanban-color-mode">
            {[{ k: "phase", l: "Por fase" }, { k: "priority", l: "Prioridad" }, { k: "owner", l: "Responsable" }].map((m) => (
              <button key={m.k} onClick={() => setColorMode(m.k)} className={`px-3 h-10 font-semibold ${colorMode === m.k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary/50"}`}>{m.l}</button>
            ))}
          </div>
          <button onClick={() => setView("kanban")} className={`px-4 h-10 rounded-sm text-sm font-semibold ${view === "kanban" ? "bg-primary text-primary-foreground" : "border border-border"}`}>Kanban</button>
          <button onClick={() => setView("table")} className={`px-4 h-10 rounded-sm text-sm font-semibold ${view === "table" ? "bg-primary text-primary-foreground" : "border border-border"}`}>Tabla</button>
        </div>
      </div>

      {view === "kanban" && (
        <div className="space-y-6">
          {Object.entries(stages.macro || {}).map(([macro, sts]) => {
            const theme = MACRO_THEMES[macro] || FALLBACK_THEME;
            const total = sts.reduce((acc, s) => acc + (byStage[s] || []).length, 0);
            const sumAmount = sts.reduce((acc, s) => acc + (byStage[s] || []).reduce((a, c) => a + (Number(c.estimated_amount) || 0), 0), 0);
            return (
              <div key={macro} className={`border ${theme.border} rounded-sm overflow-hidden`}>
                <div className={`h-1 bg-gradient-to-r ${theme.bar}`} />
                <button onClick={() => setOpen((o) => ({ ...o, [macro]: !o[macro] }))} className={`w-full flex items-center justify-between px-4 py-3 ${theme.soft} hover:opacity-90 transition-opacity`}>
                  <div className="flex items-center gap-3">
                    {open[macro] ? <ChevronDown className={`w-4 h-4 ${theme.text}`} /> : <ChevronRight className={`w-4 h-4 ${theme.text}`} />}
                    <h3 className={`font-display font-bold uppercase tracking-widest text-sm ${theme.text}`}>{macro}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${theme.chip}`}>{total} cards</span>
                    <span className="text-[10px] text-muted-foreground">≈ {sumAmount.toLocaleString()}</span>
                  </div>
                </button>
                {open[macro] && (
                  <div className="p-3 overflow-x-auto">
                    <div className="flex gap-3 min-w-max">
                      {sts.map((s) => {
                        const cards = byStage[s] || [];
                        const isOver = dragOver === s;
                        return (
                          <div
                            key={s}
                            className={`w-64 flex-shrink-0 bg-background border rounded-sm p-3 transition-all ${isOver ? "border-primary bg-primary/5 scale-[1.01]" : "border-border"}`}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(s); }}
                            onDragLeave={() => setDragOver((cur) => (cur === s ? null : cur))}
                            onDrop={(e) => onDrop(e, s)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className={`text-[10px] uppercase tracking-widest font-bold ${theme.text}`}>{STAGE_LABELS[s] || s}</h4>
                              <span className={`text-[10px] font-bold px-1.5 rounded-sm ${theme.chip}`}>{cards.length}</span>
                            </div>
                            <div className="space-y-2">
                              {cards.map((c) => {
                                const accent = cardAccent(c);
                                const due = dueState(c.due_date);
                                const owner = c.owner_email || c.assignee || c.created_by || "";
                                return (
                                  <div
                                    key={c.id}
                                    draggable
                                    onDragStart={(e) => onDragStart(e, c)}
                                    onClick={() => setSelectedCard(c)}
                                    style={{ borderLeftColor: accent }}
                                    className={`bg-secondary/20 border border-border border-l-4 p-2.5 rounded-sm cursor-grab hover:border-primary hover:shadow-md transition-all active:cursor-grabbing ring-2 ring-transparent hover:${PRIORITY_RING[c.priority] || PRIORITY_RING.normal}`}
                                    data-testid={`workflow-card-${c.id}`}
                                  >
                                    <div className="flex items-start justify-between gap-1 mb-1.5">
                                      <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[c.priority] || PRIORITY_DOT.normal}`} title={`Prioridad: ${c.priority || "normal"}`} />
                                      <p className="text-[10px] text-muted-foreground">{c.currency || "BOB"} {(c.estimated_amount || 0).toLocaleString()}</p>
                                    </div>
                                    <p className="text-sm font-semibold leading-tight mb-1">{c.title || "Sin título"}</p>
                                    <p className="text-[11px] text-muted-foreground truncate">{c.client_name || "—"}</p>
                                    <div className="flex items-center justify-between gap-1 mt-2">
                                      <div className="flex items-center gap-1">
                                        {owner && (
                                          <span className="w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center text-white" title={owner} style={{ background: colorFromString(owner) }}>{initialsOf(owner)}</span>
                                        )}
                                        {(c.comments || []).length > 0 && (
                                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {c.comments.length}</span>
                                        )}
                                        {(c.close_probability_pct || 0) > 0 && (
                                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="w-3 h-3" /> {c.close_probability_pct}%</span>
                                        )}
                                      </div>
                                      {due && (
                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm border ${due.cls}`}>
                                          {due.kind === "overdue" ? `−${due.days}d` : due.kind === "soon" ? `${due.days}d` : `${due.days}d`}
                                        </span>
                                      )}
                                    </div>
                                    {c.next_action && <p className="text-[10px] mt-1.5 italic text-muted-foreground truncate">→ {c.next_action}</p>}
                                  </div>
                                );
                              })}
                              {cards.length === 0 && (
                                <p className="text-[10px] text-muted-foreground italic text-center py-4">Soltá un card aquí</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === "table" && (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-3">Oportunidad</th><th className="text-left p-3">Cliente</th><th className="text-left p-3">Etapa</th><th className="text-left p-3">Próx. acción</th><th className="text-left p-3">Vence</th><th className="text-right p-3">Monto</th><th className="text-left p-3">Prob.</th></tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const macro = stageToMacro[c.stage] || "";
                const theme = MACRO_THEMES[macro] || FALLBACK_THEME;
                return (
                  <tr key={c.id} onClick={() => setSelectedCard(c)} className="border-t border-border hover:bg-secondary/30 cursor-pointer">
                    <td className="p-3 font-semibold">{c.title}</td>
                    <td className="p-3">{c.client_name || "—"}</td>
                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${theme.chip}`}>{STAGE_LABELS[c.stage] || c.stage}</span></td>
                    <td className="p-3 text-xs">{c.next_action || "—"}</td>
                    <td className="p-3 text-xs">{c.due_date || "—"}</td>
                    <td className="p-3 text-right">{c.currency || "BOB"} {(c.estimated_amount || 0).toLocaleString()}</td>
                    <td className="p-3 text-xs">{c.close_probability_pct || 0}%</td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Sin oportunidades. Se crean automáticamente cuando creás una propuesta.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {selectedCard && <CardPanel card={selectedCard} onClose={() => setSelectedCard(null)} onSaved={() => { setSelectedCard(null); load(); }} stages={stages} />}
    </div>
  );
}

const colorFromMacro = (m) => {
  const map = { "Lead & Armado": "#3B82F6", "Negociación": "#F59E0B", "Cierre": "#10B981", "Post-venta": "#8B5CF6" };
  return map[m] || "#71717A";
};
const colorFromPriority = (p) => ({ low: "#A1A1AA", normal: "#3B82F6", high: "#F59E0B", urgent: "#EF4444" }[p] || "#3B82F6");

function CardPanel({ card, onClose, onSaved, stages }) {
  const [c, setC] = useState(card);
  const [comment, setComment] = useState("");
  const save = async () => {
    await api.put(`/admin/com/workflow/${c.id}`, c);
    onSaved();
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/admin/com/workflow/${c.id}/comment`, { text: comment });
    setComment("");
    const reloaded = await api.get(`/admin/com/workflow`);
    const fresh = reloaded.find((x) => x.id === c.id);
    if (fresh) setC(fresh);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-end p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-xl h-full overflow-y-auto rounded-sm border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-black text-2xl">{c.title}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Etapa</label>
              <select value={c.stage} onChange={(e) => setC({ ...c, stage: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
                {(stages.stages || []).map((s) => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
              </select>
            </div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Prioridad</label>
              <select value={c.priority} onChange={(e) => setC({ ...c, priority: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
                {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Próxima acción</label>
              <input value={c.next_action || ""} onChange={(e) => setC({ ...c, next_action: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Fecha límite</label>
              <input type="date" value={c.due_date || ""} onChange={(e) => setC({ ...c, due_date: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Cierre estimado</label>
              <input type="date" value={c.estimated_close_date || ""} onChange={(e) => setC({ ...c, estimated_close_date: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Monto estimado</label>
              <input type="number" value={c.estimated_amount || 0} onChange={(e) => setC({ ...c, estimated_amount: Number(e.target.value) })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Probabilidad de cierre %</label>
              <input type="number" value={c.close_probability_pct || 0} onChange={(e) => setC({ ...c, close_probability_pct: Number(e.target.value) })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
            <div className="col-span-2"><label className="text-[10px] uppercase tracking-widest font-bold block mb-1 flex items-center gap-1"><UserIcon className="w-3 h-3" /> Responsable (email)</label>
              <input value={c.owner_email || ""} placeholder="ejecutivo@influcastbo.com" onChange={(e) => setC({ ...c, owner_email: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
          </div>
          <button onClick={save} className="w-full px-4 h-10 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">Guardar cambios</button>

          <div className="border-t border-border pt-4">
            <h4 className="font-display font-bold mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Comentarios ({(c.comments || []).length})</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
              {(c.comments || []).map((com, i) => (
                <div key={i} className="bg-secondary/30 p-2 rounded-sm text-xs">
                  <p>{com.text}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{com.by} · {com.at?.slice(0, 16).replace("T", " ")}</p>
                </div>
              ))}
              {(c.comments || []).length === 0 && <p className="text-xs italic text-muted-foreground">Sin comentarios.</p>}
            </div>
            <div className="flex gap-2">
              <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Agregar comentario..." className="flex-1 h-9 px-2 bg-background border border-border rounded-sm text-xs" />
              <button onClick={addComment} className="px-3 h-9 bg-primary text-primary-foreground rounded-sm text-xs font-semibold">Enviar</button>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h4 className="font-display font-bold mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Historial ({(c.history || []).length})</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {(c.history || []).map((h, i) => (
                <p key={i} className="text-[11px] text-muted-foreground border-l-2 border-primary pl-2">
                  → {STAGE_LABELS[h.stage] || h.stage} · {h.by} · {h.at?.slice(0, 16).replace("T", " ")}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
